// The admin's brand colour (Preferences → Branding → Primary Color). The
// theme's emerald green is the default; a saved colour is applied to the
// admin as CSS variables on <html> (primary + shade / tint / contrast, the
// toolbar and app-bar tokens) so every green in the admin follows it.
// The /app side and the marketing pages keep the theme's own colour: the
// admin layout applies the override while it is mounted and clears it after.

export const DEFAULT_BRAND_COLOR = "#34d399";
// One key per scope, and the app's is per account: the admin's colour and a
// customer's are different brands, and a shared browser must not paint the
// previous person's colour over the next one's.
const STORAGE_KEYS = { admin: "adminBrandColor", app: "appBrandColor" };
const storageKey = (scope, uid) => (scope === "app" ? `${STORAGE_KEYS.app}:${uid || "anon"}` : STORAGE_KEYS.admin);

export const isHex = (v) => /^#[0-9a-f]{6}$/i.test(String(v || "").trim());

// "#abc" / "abc" / "#aabbcc" → "#aabbcc" (lower-case), or null
export function normalizeHex(v) {
  let s = String(v || "").trim().replace(/^#/, "");
  if (/^[0-9a-f]{3}$/i.test(s)) s = s.split("").map((c) => c + c).join("");
  return /^[0-9a-f]{6}$/i.test(s) ? `#${s.toLowerCase()}` : null;
}

export function hexToRgb(hex) {
  const h = normalizeHex(hex) || DEFAULT_BRAND_COLOR;
  return { r: parseInt(h.slice(1, 3), 16), g: parseInt(h.slice(3, 5), 16), b: parseInt(h.slice(5, 7), 16) };
}

const clamp = (n) => Math.max(0, Math.min(255, Math.round(n)));
export function rgbToHex({ r, g, b }) {
  return `#${[r, g, b].map((n) => clamp(n).toString(16).padStart(2, "0")).join("")}`;
}

// mix a colour toward another (0 = colour, 1 = other)
export function mix(hex, otherHex, amount) {
  const a = hexToRgb(hex), b = hexToRgb(otherHex);
  return rgbToHex({ r: a.r + (b.r - a.r) * amount, g: a.g + (b.g - a.g) * amount, b: a.b + (b.b - a.b) * amount });
}
export const shade = (hex, amount = 0.12) => mix(hex, "#000000", amount);
export const tint = (hex, amount = 0.1) => mix(hex, "#ffffff", amount);

// white or black text on the colour (WCAG relative luminance)
export function contrastOn(hex) {
  const { r, g, b } = hexToRgb(hex);
  const lin = (c) => { const s = c / 255; return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4; };
  const L = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  return L > 0.42 ? "#000000" : "#ffffff";
}

// ── HSV, for the picker ──
export function hexToHsv(hex) {
  const { r, g, b } = hexToRgb(hex);
  const R = r / 255, G = g / 255, B = b / 255;
  const max = Math.max(R, G, B), min = Math.min(R, G, B), d = max - min;
  let h = 0;
  if (d) {
    if (max === R) h = ((G - B) / d) % 6;
    else if (max === G) h = (B - R) / d + 2;
    else h = (R - G) / d + 4;
    h *= 60; if (h < 0) h += 360;
  }
  return { h, s: max ? d / max : 0, v: max };
}
export function hsvToHex({ h, s, v }) {
  const c = v * s, x = c * (1 - Math.abs(((h / 60) % 2) - 1)), m = v - c;
  let r = 0, g = 0, b = 0;
  if (h < 60) [r, g, b] = [c, x, 0]; else if (h < 120) [r, g, b] = [x, c, 0]; else if (h < 180) [r, g, b] = [0, c, x];
  else if (h < 240) [r, g, b] = [0, x, c]; else if (h < 300) [r, g, b] = [x, 0, c]; else [r, g, b] = [c, 0, x];
  return rgbToHex({ r: (r + m) * 255, g: (g + m) * 255, b: (b + m) * 255 });
}

// The CSS variables a colour sets: Ionic's primary family, the toolbar and
// the app-bar tokens the admin chrome reads
// WCAG contrast ratio between two colours (1 = identical, 21 = black on white)
export function contrastRatio(a, b) {
  const lum = (hex) => {
    const { r, g, b: bb } = hexToRgb(hex);
    const lin = (c) => { const s = c / 255; return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4; };
    return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(bb);
  };
  const [x, y] = [lum(a), lum(b)].sort((m, n) => n - m);
  return (x + 0.05) / (y + 0.05);
}

export function brandVars(hex) {
  const h = normalizeHex(hex) || DEFAULT_BRAND_COLOR;
  const { r, g, b } = hexToRgb(h);
  // The chrome on the colour (top bar icons and title, button text) stays
  // white whatever the colour, as in the theme. Deriving it from the colour
  // was tried and reverted: every mint, teal or amber someone would actually
  // pick is light enough to flip it to black, which is not what this app
  // looks like. The picker warns when white on a colour reads poorly instead.
  const contrast = "#ffffff";
  const cr = hexToRgb(contrast);
  return {
    "--ion-color-primary": h,
    "--ion-color-primary-rgb": `${r}, ${g}, ${b}`,
    "--ion-color-primary-contrast": contrast,
    "--ion-color-primary-contrast-rgb": `${cr.r}, ${cr.g}, ${cr.b}`,
    "--ion-color-primary-shade": shade(h),
    "--ion-color-primary-tint": tint(h),
    "--ion-toolbar-background": h,
    "--ion-toolbar-color": contrast,
    "--app-bar-color": h,
  };
}

// The sign page's backdrop and the branding previews' sign-in screen:
// stacked waves of the brand colour, darkest at the top left and easing to
// a pale tint at the bottom right, drawn as an inline SVG so the band
// edges curve. --brand-gradient carries the image; --brand-zig-color is
// the flat fallback behind it.
export function brandWavesVars(hex) {
  const h = normalizeHex(hex) || DEFAULT_BRAND_COLOR;
  const tones = [0, 0.14, 0.27, 0.4, 0.52, 0.64, 0.78].map((a) => tint(h, a));
  // one band = everything below a gently waving boundary (left y -> right y,
  // sagging before cresting toward the right). The last two start below the
  // bottom edge and crest into view at the bottom right, the same wave as the
  // rest (drawn as plain arcs they read as straight diagonals on a wide
  // screen); the bands close well below the frame so a crest can rise into it
  const wave = (yl, yr, c) =>
    `<path fill='${c}' d='M-20 ${yl} C 430 ${yl + 140}, 1090 ${yr - 150}, 1620 ${yr} L 1620 1300 L -20 1300 Z'/>`;
  const svg =
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1600 900' preserveAspectRatio='xMidYMid slice'>` +
    `<rect x='-20' y='-20' width='1640' height='1340' fill='${tones[0]}'/>` +
    wave(215, 95, tones[1]) + wave(430, 300, tones[2]) + wave(635, 495, tones[3]) + wave(830, 680, tones[4]) +
    wave(990, 790, tones[5]) + wave(1150, 900, tones[6]) +
    `</svg>`;
  return {
    "--brand-zig-color": tones[1],
    "--brand-gradient": `url("data:image/svg+xml,${encodeURIComponent(svg)}")`,
  };
}

export function readStoredBrandColor(scope = "admin", uid) {
  try { return normalizeHex(localStorage.getItem(storageKey(scope, uid))) || null; } catch { return null; }
}
export function storeBrandColor(hex, scope = "admin", uid) {
  try {
    const key = storageKey(scope, uid);
    const h = normalizeHex(hex);
    if (h && h !== DEFAULT_BRAND_COLOR) localStorage.setItem(key, h); else localStorage.removeItem(key);
  } catch { /* storage off */ }
}
export function forgetBrandColor(scope = "admin", uid) {
  try { localStorage.removeItem(storageKey(scope, uid)); } catch { /* storage off */ }
}

// Set / clear the override on <html>. The admin and /app each apply their own
// while their layout is mounted; they are never mounted together.
let current = null; // what brandPrimary() answers without a style lookup

export function applyBrandColor(hex) {
  const root = document.documentElement;
  const h = normalizeHex(hex);
  if (!h || h === DEFAULT_BRAND_COLOR) { clearBrandColor(); return; }
  Object.entries(brandVars(h)).forEach(([k, v]) => root.style.setProperty(k, v));
  root.setAttribute("data-brand-color", h);
  current = h;
}
export function clearBrandColor() {
  const root = document.documentElement;
  Object.keys(brandVars(DEFAULT_BRAND_COLOR)).forEach((k) => root.style.removeProperty(k));
  root.removeAttribute("data-brand-color");
  current = null;
}
// The admin's long-standing names, kept so its call sites read unchanged
export const applyAdminBrandColor = applyBrandColor;
export const clearAdminBrandColor = clearBrandColor;

// The admin's SITE colour (Admin → Preferences → Branding) doubles as the
// default brand colour for /app: a user without their own colour wears it.
// Cached so the first paint after a reload is already right.
const SITE_DEFAULT_KEY = "siteDefaultBrandColor";
export function storeSiteDefaultBrandColor(hex) {
  const h = normalizeHex(hex);
  try {
    if (h && h !== DEFAULT_BRAND_COLOR) localStorage.setItem(SITE_DEFAULT_KEY, h);
    else localStorage.removeItem(SITE_DEFAULT_KEY);
  } catch { /* storage blocked */ }
}
export function siteDefaultBrandColor() {
  try { return normalizeHex(localStorage.getItem(SITE_DEFAULT_KEY)); } catch { return null; }
}

// The brand colour as a hex string, for the places that need a literal
// rather than a CSS variable: chart fills and gradients, colour maps,
// the template editor's default badge colour. Read at render time so it
// follows the override the layout applies.
export function brandPrimary() {
  if (current) return current;
  try {
    const v = normalizeHex(getComputedStyle(document.documentElement).getPropertyValue("--ion-color-primary"));
    if (v) return v;
  } catch { /* no DOM */ }
  return DEFAULT_BRAND_COLOR;
}
